import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { MutationRefetchQueries } from "~/common/types";
import { config } from "~/config";
import {
  CreateBatchEvidencesJobDocument,
  type CreateBatchEvidencesJobMutation,
  type CreateBatchEvidencesJobMutationVariables,
  UpdateBatchEvidencesJobDocument,
  type UpdateBatchEvidencesJobMutation,
  type UpdateBatchEvidencesJobMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { BatchEvidencesJobFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface Props {
  importProjectId: string;
  dataSourceId: string;
  batchEvidencesJobId?: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
  onSuccess: () => void;
}

export function useBatchEvidencesJobForm({
  importProjectId,
  dataSourceId,
  batchEvidencesJobId,
  refetchQueriesOnEdit,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const { tenantId } = useTenant();
  const [updateBatchEvidencesJob] = useMutation<
    UpdateBatchEvidencesJobMutation,
    UpdateBatchEvidencesJobMutationVariables
  >(UpdateBatchEvidencesJobDocument, {
    refetchQueries: refetchQueriesOnEdit,
  });
  const [createBatchEvidencesJob] = useMutation<
    CreateBatchEvidencesJobMutation,
    CreateBatchEvidencesJobMutationVariables
  >(CreateBatchEvidencesJobDocument, {
    refetchQueries: refetchQueriesOnEdit,
  });

  const submit = useCallback(
    async (formData: BatchEvidencesJobFormData) => {
      const progressToastId = toast.loading(t("ui.processing"));
      setProcessing(true);

      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        // The form holds "" for "no importer"; the field is a nullable UUID, so send it as absent.
        const evidenceImporterId = formData.evidenceImporterId || undefined;

        const fn = batchEvidencesJobId
          ? updateBatchEvidencesJob({
              variables: {
                input: {
                  tenantId,
                  id: batchEvidencesJobId,
                  name: formData.name,
                  evidenceImporterId,
                },
              },
            })
          : createBatchEvidencesJob({
              variables: {
                input: {
                  tenantId,
                  dataImportProjectId: importProjectId,
                  dataSourceId,
                  name: formData.name,
                  evidenceImporterId,
                },
              },
            });

        await fn;

        const successMsg = batchEvidencesJobId
          ? t("feature.common.updatedSuccessfully", { model: "batchEvidencesJob" })
          : t("feature.common.createdSuccessfully", { model: "batchEvidencesJob" });

        toast.success(successMsg);
        onSuccess();
      } catch (e) {
        if (e instanceof Error) {
          toast.error(translateError(t, e));
        }
      } finally {
        toast.dismiss(progressToastId);
        setProcessing(false);
      }
    },
    [
      tenantId,
      importProjectId,
      dataSourceId,
      batchEvidencesJobId,
      updateBatchEvidencesJob,
      createBatchEvidencesJob,
      onSuccess,
      t,
    ],
  );

  return {
    submit,
    processing,
  };
}
